// =============================================================================
// BP-07-03: Resource Store (Zustand)
// =============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ResourceFilterState, ResourceViewState, ResourceType, LifeStage } from '@/features/resources/types/resourcesTypes';

// -----------------------------------------------------------------------------
// Filter Store
// -----------------------------------------------------------------------------

interface ResourceFilterStore extends ResourceFilterState {
  // Actions
  setCategorySlug: (slug: string | null) => void;
  setDiagnosisSlug: (slug: string | null) => void;
  setResourceType: (type: ResourceType | null) => void;
  setLifeStage: (stage: LifeStage | null) => void;
  setAgeRange: (range: [number, number] | null) => void;
  addTag: (tag: string) => void;
  removeTag: (tag: string) => void;
  setSearchQuery: (query: string) => void;
  clearFilters: () => void;
  setFilters: (filters: Partial<ResourceFilterState>) => void;
}

const initialFilterState: ResourceFilterState = {
  categorySlug: null,
  diagnosisSlug: null,
  resourceType: null,
  lifeStage: null,
  ageRange: null,
  tags: [],
  searchQuery: '',
};

export const useResourceFilterStore = create<ResourceFilterStore>()((set) => ({
  ...initialFilterState,

  setCategorySlug: (slug) => set({ categorySlug: slug }),
  setDiagnosisSlug: (slug) => set({ diagnosisSlug: slug }),
  setResourceType: (type) => set({ resourceType: type }),
  setLifeStage: (stage) => set({ lifeStage: stage }),
  setAgeRange: (range) => set({ ageRange: range }),
  
  addTag: (tag) =>
    set((state) => ({
      tags: state.tags.includes(tag) ? state.tags : [...state.tags, tag],
    })),
  
  removeTag: (tag) =>
    set((state) => ({
      tags: state.tags.filter((t) => t !== tag),
    })),
  
  setSearchQuery: (query) => set({ searchQuery: query }),
  
  clearFilters: () => set(initialFilterState),
  
  setFilters: (filters) => set((state) => ({ ...state, ...filters })),
}));

// -----------------------------------------------------------------------------
// View Store (Persisted)
// -----------------------------------------------------------------------------

interface ResourceViewStore extends ResourceViewState {
  // Actions
  setViewMode: (mode: 'grid' | 'list') => void;
  setSortBy: (sort: ResourceViewState['sortBy']) => void;
  addToRecentlyViewed: (resourceId: string) => void;
  clearRecentlyViewed: () => void;
  updateReadingProgress: (resourceId: string, percentage: number) => void;
  clearReadingProgress: (resourceId: string) => void;
}

const MAX_RECENTLY_VIEWED = 20;

export const useResourceViewStore = create<ResourceViewStore>()(
  persist(
    (set) => ({
      viewMode: 'grid',
      sortBy: 'newest',
      recentlyViewed: [],
      readingProgress: {},

      setViewMode: (mode) => set({ viewMode: mode }),
      
      setSortBy: (sortBy) => set({ sortBy }),
      
      addToRecentlyViewed: (resourceId) =>
        set((state) => {
          const filtered = state.recentlyViewed.filter((id) => id !== resourceId);
          return {
            recentlyViewed: [resourceId, ...filtered].slice(0, MAX_RECENTLY_VIEWED),
          };
        }),
      
      clearRecentlyViewed: () => set({ recentlyViewed: [] }),
      
      updateReadingProgress: (resourceId, percentage) =>
        set((state) => ({
          readingProgress: {
            ...state.readingProgress,
            [resourceId]: Math.min(100, Math.max(0, percentage)),
          },
        })),
      
      clearReadingProgress: (resourceId) =>
        set((state) => {
          const { [resourceId]: _, ...rest } = state.readingProgress;
          return { readingProgress: rest };
        }),
    }),
    {
      name: 'brightpath-resources-view',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        viewMode: state.viewMode,
        sortBy: state.sortBy,
        recentlyViewed: state.recentlyViewed,
        readingProgress: state.readingProgress,
      }),
    }
  )
);

// -----------------------------------------------------------------------------
// Search History Store (Persisted)
// -----------------------------------------------------------------------------

interface SearchHistoryStore {
  searchHistory: string[];
  addToHistory: (query: string) => void;
  removeFromHistory: (query: string) => void;
  clearHistory: () => void;
}

const MAX_SEARCH_HISTORY = 10;

export const useSearchHistoryStore = create<SearchHistoryStore>()(
  persist(
    (set) => ({
      searchHistory: [],

      addToHistory: (query) =>
        set((state) => {
          const trimmed = query.trim();
          if (!trimmed || trimmed.length < 2) return state;
          
          const filtered = state.searchHistory.filter(
            (q) => q.toLowerCase() !== trimmed.toLowerCase()
          );
          return {
            searchHistory: [trimmed, ...filtered].slice(0, MAX_SEARCH_HISTORY),
          };
        }),
      
      removeFromHistory: (query) =>
        set((state) => ({
          searchHistory: state.searchHistory.filter((q) => q !== query),
        })),
      
      clearHistory: () => set({ searchHistory: [] }),
    }),
    {
      name: 'brightpath-search-history',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// -----------------------------------------------------------------------------
// Selector Hooks
// -----------------------------------------------------------------------------

/**
 * Get active filter count
 */
export function useActiveFilterCount(): number {
  const filters = useResourceFilterStore();
  let count = 0;
  
  if (filters.categorySlug) count++;
  if (filters.diagnosisSlug) count++;
  if (filters.resourceType) count++;
  if (filters.lifeStage) count++;
  if (filters.ageRange) count++;
  count += filters.tags.length;
  
  return count;
}

/**
 * Check if any filters are active
 */
export function useHasActiveFilters(): boolean {
  return useActiveFilterCount() > 0;
}

/**
 * Get filters as API params
 */
export function useFiltersAsParams() {
  const filters = useResourceFilterStore();
  
  return {
    categorySlug: filters.categorySlug ?? undefined,
    diagnosisSlug: filters.diagnosisSlug ?? undefined,
    resourceType: filters.resourceType ?? undefined,
    lifeStage: filters.lifeStage ?? undefined,
    ageMin: filters.ageRange?.[0],
    ageMax: filters.ageRange?.[1],
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    search: filters.searchQuery || undefined,
  };
}

// Resource Store (alias for ResourceFilterStore)
export const useResourceStore = useResourceFilterStore;

// View Store alias
export const useViewStore = useResourceViewStore;

